import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { LockIcon } from '../../icons';

import styles from './_index.module.scss';

export interface OptionTabsProps extends BaseTabsProps {
  'aria-label': string;
  onChange?: (value: number | string) => void;
  options: Array<{ isDisabled?: boolean; label: React.ReactNode; value: number | string }>;
  routes?: never;
  value?: number | string;
}

export type TabsProps = EmptyTabsProps | OptionTabsProps | RouteTabsProps;

interface BaseTabsProps {
  className?: string;
  size?: 'default' | 'small';
  variant?: 'default' | 'rounded';
}

interface EmptyTabsProps extends BaseTabsProps {
  'aria-label'?: never;
  onChange?: never;
  options?: never;
  routes?: never;
  value?: never;
}

interface RouteTabsProps extends BaseTabsProps {
  'aria-label'?: never;
  onChange?: never;
  options?: never;
  routes: Array<{
    is_active?: boolean;
    is_locked?: boolean;
    isDisabled?: boolean;
    label: React.ReactNode;
    to: string | { hash?: string; pathname: string; search?: string };
  }>;
  value?: never;
}

const Tabs = ({
  'aria-label': ariaLabel,
  className,
  onChange = () => {},
  options,
  routes,
  size = 'default',
  value,
  variant = 'default',
}: TabsProps) => {
  const { pathname } = useLocation();
  const tabsClassName = classNames(
    styles['tabs'],
    styles[variant],
    {
      [styles['size-small']]: size === 'small',
    },
    className
  );

  if (routes?.length) {
    return (
      <ul className={tabsClassName}>
        {routes.map((route, index) => {
          const url = typeof route.to === 'object' ? route.to.pathname : route.to;
          const isActive = pathname?.includes(url) || route?.is_active;
          const isUnavailable = route?.isDisabled || route?.is_locked;

          const listClassNames = classNames({
            [styles['is-active']]: isActive,
            [styles['is-disabled']]: route?.isDisabled,
            [styles['is-locked']]: route?.is_locked,
          });

          return (
            <li className={listClassNames} data-testid={`tab-${index}`} key={`route-${index}`}>
              {isUnavailable ? (
                <span aria-current={isActive ? 'page' : undefined} aria-disabled="true">
                  {route?.label}
                </span>
              ) : (
                <Link aria-current={isActive ? 'page' : undefined} replace to={route?.to}>
                  {route?.label}
                </Link>
              )}
              {route?.is_locked && <LockIcon aria-hidden="true" />}
            </li>
          );
        })}
      </ul>
    );
  }

  if (options?.length) {
    return (
      <div aria-label={ariaLabel} className={tabsClassName} role="group">
        {options?.map((option, index) => {
          const buttonClassNames = classNames({
            [styles['is-active']]: option?.value === value,
            [styles['is-disabled']]: option?.isDisabled,
          });

          return (
            <button
              aria-pressed={option?.value === value}
              className={buttonClassNames}
              data-testid={`option-${index}`}
              disabled={option?.isDisabled}
              key={`option-${index}`}
              onClick={() => onChange(option?.value)}
              type="button"
            >
              {option?.label}
            </button>
          );
        })}
      </div>
    );
  }

  return null;
};

export default Tabs;
